import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowUpRight, ArrowRight, Shield, Flame, Ambulance, Building2, Radio, GraduationCap } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const ROLES = [
  { icon: Shield, title: "Law enforcement", text: "Threat suppression, warm-zone escort, command integration." },
  { icon: Flame, title: "Fire / Rescue", text: "Rescue task force, extraction, casualty collection points." },
  { icon: Ambulance, title: "EMS", text: "Treatment, triage, transport decisions, NEMSIS documentation." },
  { icon: Building2, title: "Trauma center", text: "Receiving expectations, alert thresholds, handoff standard." },
  { icon: Radio, title: "Dispatch / Comms", text: "Notification, common terminology, resource tracking, timestamps." },
  { icon: GraduationCap, title: "Academy / Simulation", text: "Facilities, instructors, scenario control, scoring capacity." },
];

const TIMELINE = [
  { period: "Months 1–3", work: "Secure governance, medical direction, MOUs, evaluator, baseline protocols, data-use agreements.", gate: "Signed agreements; named leads; approved evaluation protocol." },
  { period: "Months 3–5", work: "Build the Common Readiness Crosswalk, scenario package, data dictionary, instructor rubric.", gate: "Clinical and operational review complete; version 1.0 released." },
  { period: "Months 5–7", work: "Collect baseline skills and multi-agency exercise data; certify the initial instructor cadre locally.", gate: "Baseline report; inter-rater check; instructor observation records." },
  { period: "Months 7–11", work: "Deliver role-based cohorts and joint simulations; run after-action and corrective-action cycles.", gate: "Training records; scored exercises; corrective-action register." },
  { period: "Months 11–14", work: "Repeat measurements at three and six months; assess retention and protocol drift.", gate: "Retention report; site comparisons; data-quality audit." },
  { period: "Months 14–17", work: "Run the capstone multi-agency exercise and independent analysis.", gate: "Prespecified outcome analysis; limitations; stakeholder interviews." },
  { period: "Month 18", work: "Publish the independent evaluation and make the go/no-go decision on replication.", gate: "Shareable report; revised toolkit; documented expansion decision." },
];

const MEASURES = [
  "Time to effective hemorrhage control", "Protocol adherence", "Triage concordance",
  "Warm-zone integration", "Handoff completeness", "Trauma-alert interval",
  "Skill retention", "Corrective-action closure", "Data completeness",
];

export default function Pilot() {
  const tlRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: tlRef, offset: ["start 0.7", "end 0.8"] });

  return (
    <main data-testid="pilot-page">
      <PageHead
        chapter="04"
        eyebrow="Pilot Model"
        titleLines={["An 18-month, independently", "evaluated regional pilot."]}
        lede="The pilot exists to answer one question: can a shared readiness framework be implemented, measured reliably, retained over time, and accepted by the institutions that would have to live with it?"
        image={IMAGES.jointExercise}
      />

      {/* PARTICIPANT PROFILE */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="participants-section">
        <Reveal>
          <Eyebrow>Proposed participant profile</Eyebrow>
          <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight max-w-3xl" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            Six functional roles, one founding region.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
            A founding region should include an urban or suburban law enforcement agency, fire/rescue, EMS, a receiving hospital or trauma center, a dispatch and communications partner, and an academy or simulation facility. A rural partner should be included where transport times and staffing permit a meaningful comparison.
          </p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
          {ROLES.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.06} className="bg-ink">
              <div data-testid={`role-card-${i}`} className="group p-8 h-full hover:bg-surface transition-colors duration-300">
                <r.icon size={22} className="text-signal" />
                <h3 className="mt-5 font-display font-bold text-xl text-white tracking-tight">{r.title}</h3>
                <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-mist">
            Target scale: 3–6 institutions · Cohort sized to local capacity · Rural comparator where feasible
          </p>
          <div className="mt-10 border-l-2 border-signal bg-surface p-6 max-w-3xl">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">On numbers that do not yet exist</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              Participant counts, costs, and site totals must be derived from written commitments and a costed budget. Until partners sign, the honest figures are ranges and the honest status is "proposed."
            </p>
          </div>
        </Reveal>
      </section>

      {/* TIMELINE */}
      <section className="border-y border-line bg-surface py-24" data-testid="timeline-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Timeline</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              Seven phases, each behind a stage gate.
            </h2>
          </Reveal>
          <div ref={tlRef} className="mt-16 relative pl-8 lg:pl-12">
            <div className="absolute left-0 top-0 bottom-0 w-px bg-line" />
            <motion.div
              className="absolute left-0 top-0 bottom-0 w-px bg-signal origin-top"
              style={{ scaleY: scrollYProgress }}
            />
            <div className="space-y-12">
              {TIMELINE.map((t, i) => (
                <Reveal key={i} delay={0.05}>
                  <div data-testid={`timeline-phase-${i}`} className="relative group">
                    <span className="absolute -left-8 lg:-left-12 top-1.5 -translate-x-1/2 w-3 h-3 bg-ink border-2 border-signal rotate-45 group-hover:bg-signal transition-colors" />
                    <span className="font-mono text-xs text-signal uppercase tracking-[0.2em]">{t.period}</span>
                    <div className="mt-3 grid lg:grid-cols-2 gap-6 border border-line bg-ink p-6 lg:p-8 hover:border-slate-600 transition-colors">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-2">Work</p>
                        <p className="text-sm text-slate-300 leading-relaxed">{t.work}</p>
                      </div>
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist mb-2">Stage-gate evidence required to proceed</p>
                        <p className="text-sm text-slate-400 leading-relaxed">{t.gate}</p>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MEASURES */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="measures-section">
        <Reveal>
          <Eyebrow>Proposed measures</Eyebrow>
          <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight max-w-3xl" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
            Nine proposed measures. All proposed, not validated.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed max-w-2xl">
            Each has an operational definition and a validation plan — establishing their reliability is itself part of the pilot's work.
          </p>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
          {MEASURES.map((m, i) => (
            <Reveal key={m} delay={i * 0.04} className="bg-ink">
              <div data-testid={`measure-${i}`} className="group flex items-center gap-4 p-6 hover:bg-surface transition-colors">
                <span className="font-mono text-xs text-signal">M{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display font-bold text-white tracking-tight">{m}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-xs text-mist leading-relaxed max-w-2xl">
          All proposed measures remain subject to local clinical approval, feasibility testing, legal review, and reliability analysis.
        </p>
      </section>

      {/* AGREEMENT */}
      <section className="border-t border-line bg-surface py-24 relative overflow-hidden" data-testid="agreement-section">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${IMAGES.hospitalHandoff})` }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Commitment</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight max-w-3xl" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              What a partner institution is actually agreeing to.
            </h2>
            <div className="mt-8 grid lg:grid-cols-2 gap-10 max-w-5xl">
              <p className="text-sm text-slate-300 leading-relaxed">
                Participation is bounded and reversible. A partner agrees to name an operational lead, contribute staff time for training and exercises, permit scored observation under agreed rules, and review outputs before publication.
              </p>
              <p className="text-sm text-slate-300 leading-relaxed">
                A partner does not surrender command authority, does not adopt clinical content its medical director has not approved, and does not release patient-identifiable data. Any institution may withdraw without penalty, and the pilot is designed so that withdrawal of one partner does not invalidate the others' data.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="pilot-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Request More Information <ArrowUpRight size={15} />
              </Link>
              <Link to="/partnerships" data-testid="pilot-cta-partnerships" className="inline-flex items-center gap-3 border border-slate-500 hover:border-white text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
                Explore Partnership Options <ArrowRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
